import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
// import QrCodeOutlinedIcon from '@mui/icons-material/QrCodeOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined';

import axios from 'axios';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import BrandingsService from '@services/BrandingsService';
import EventsService from '@services/EventsService';
import { env } from 'env';

import {
  EditorGroupId,
  EditorNodeKind,
  EDITOR_GROUPS,
  SpotType,
  getSpotType,
} from '../editorCategories';
import { useEditorMode } from '../EditorModeContext';
import { useEditorStyles } from '../styles';
import { useSceneTreeStyles } from './styles';

type AnySpot = Record<string, unknown> & {
  ueIdentifier?: string;
  name?: string;
  isActive?: boolean;
  type?: string;
};

const getSpotTypeIcon = (type: SpotType, sx: object) => {
  if (type === 'VIDEO') {
    return <PlayArrowOutlinedIcon sx={sx} />;
  }

  return <ImageOutlinedIcon sx={sx} />;
};

const normalizeUeIdentifier = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

const SceneTree = () => {
  const { t: translate } = useTranslation();
  const styles = useEditorStyles();
  const treeStyles = useSceneTreeStyles();

  const {
    selectedNode,
    selectMapAndColors,
    selectSpot,
    isSceneTreeCollapsed,
    toggleSceneTree,
    setAutosaveStatus,
  } = useEditorMode();

  const [collapsedGroups, setCollapsedGroups] = useState<
    Partial<Record<EditorGroupId, boolean>>
  >({});

  const toggleGroup = useCallback((id: EditorGroupId) => {
    setCollapsedGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const {
    roomSpots,
    setRoomSpots,
    brandingSpots,
    setBrandingSpots,
    currentRoom,
    currentBrandingId,
    isBrandingModeEnabled,
    setSelectedCustomizationUeId,
    setSelectedCustomizationTag,
    setSelectedCustomizationSpotType,
    setSelectedCustomizationTargetSize,
  } = useMytaverseEvent();
  const { sendMessageToUnreal } = useMytaverse();

  const handleSpotSelect = useCallback(
    (spot: AnySpot, ueId: string) => {
      const type = getSpotType(spot);
      setSelectedCustomizationUeId(ueId);
      setSelectedCustomizationTag(ueId);
      setSelectedCustomizationSpotType(type);

      setSelectedCustomizationTargetSize(null);
      selectSpot(ueId);
    },
    [
      selectSpot,
      setSelectedCustomizationUeId,
      setSelectedCustomizationTag,
      setSelectedCustomizationSpotType,
      setSelectedCustomizationTargetSize,
    ],
  );

  const spots = useMemo<AnySpot[]>(() => {
    const source =
      currentBrandingId && isBrandingModeEnabled ? brandingSpots : roomSpots;
    return Array.isArray(source) ? (source as AnySpot[]) : [];
  }, [currentBrandingId, isBrandingModeEnabled, brandingSpots, roomSpots]);

  const isMapColorsSelected =
    selectedNode?.kind === EditorNodeKind.MAP_AND_COLORS;

  const selectedSpotUeId =
    selectedNode?.kind === EditorNodeKind.SPOT
      ? selectedNode.ueIdentifier
      : null;

  const selectedNodeRef = useRef<HTMLDivElement | null>(null);
  const spotsCollapsed = !!collapsedGroups[EditorGroupId.SPOTS];

  useEffect(() => {
    if (!selectedSpotUeId) {
      return;
    }
    setCollapsedGroups((prev) => {
      if (!prev[EditorGroupId.SPOTS]) {
        return prev;
      }
      return { ...prev, [EditorGroupId.SPOTS]: false };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSpotUeId]);

  useEffect(() => {
    if (!selectedSpotUeId || spotsCollapsed) {
      return;
    }
    const node = selectedNodeRef.current;
    if (node) {
      node.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedSpotUeId, spotsCollapsed, spots.length]);

  const handleToggleVisibility = useCallback(
    async (e: React.MouseEvent, spot: AnySpot) => {
      e.stopPropagation();
      const nextActive = spot.isActive === false;
      const useBrandingSpots = !!currentBrandingId && isBrandingModeEnabled;

      const brandingSpotId =
        typeof (spot as { brandingSpotId?: unknown }).brandingSpotId ===
        'string'
          ? ((spot as { brandingSpotId: string }).brandingSpotId as string)
          : null;
      const roomSpotId =
        typeof (spot as { roomSpotId?: unknown }).roomSpotId === 'string'
          ? ((spot as { roomSpotId: string }).roomSpotId as string)
          : null;
      const fallbackId =
        typeof (spot as { id?: unknown }).id === 'string'
          ? ((spot as { id: string }).id as string)
          : null;

      setAutosaveStatus('saving');
      try {
        if (useBrandingSpots && currentBrandingId) {
          const id = brandingSpotId || fallbackId;
          if (!id) {
            throw new Error('Missing branding spot id for visibility toggle');
          }
          await BrandingsService.patchBrandingSpot(currentBrandingId, id, {
            isActive: nextActive,
          });
          const refreshed = await BrandingsService.getBrandingSpots(
            currentBrandingId,
            { limit: 200 },
          );
          setBrandingSpots(refreshed.spots ?? []);
        } else if (currentRoom) {
          const id = roomSpotId || fallbackId;
          if (!id) {
            throw new Error('Missing room spot id for visibility toggle');
          }

          await axios.patch(
            `${env.REACT_APP_API_URL}api/v2/rooms/${currentRoom.roomId}/spots/${id}`,
            { isActive: nextActive },
          );
          const refreshed = await EventsService.getRoomSpots(
            currentRoom.roomId,
          );
          setRoomSpots(refreshed);
        }
        sendMessageToUnreal({
          action: 'SPOTS_UPDATED',
          spot: { ...spot, isActive: nextActive },
        });
        setAutosaveStatus('saved');
      } catch (toggleError) {
        console.error(
          `Failed to toggle spot visibility: ${
            (toggleError as Error)?.message
          }`,
          {
            ueIdentifier:
              typeof spot.ueIdentifier === 'string' ? spot.ueIdentifier : null,
            brandingId: currentBrandingId,
            roomId: currentRoom?.roomId,
          },
        );
        setAutosaveStatus('error');
      }
    },
    [
      currentBrandingId,
      currentRoom,
      isBrandingModeEnabled,
      sendMessageToUnreal,
      setAutosaveStatus,
      setBrandingSpots,
      setRoomSpots,
    ],
  );

  if (isSceneTreeCollapsed) {
    return (
      <Box
        sx={[styles.glassPanel, styles.sceneTreeCollapsedPill]}
        onClick={toggleSceneTree}
      >
        <ViewSidebarOutlinedIcon fontSize="small" />
        <Typography sx={{ fontSize: '13px', color: 'inherit' }}>
          {translate('editorMode.scene', 'Scene')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={[styles.glassPanel, styles.sceneTreeWrap, { overflow: 'hidden' }]}>
      <Box sx={styles.panelHeader}>
        <Box sx={styles.panelTitleRow}>
          <LayersOutlinedIcon sx={styles.panelTitleIcon} />
          <Typography sx={styles.panelTitle}>
            {translate('editorMode.scene', 'Scene')}
          </Typography>
        </Box>
        <IconButton
          onClick={toggleSceneTree}
          sx={styles.panelIconButton}
          size="small"
          aria-label={translate('editorMode.collapseTree', 'Collapse scene')}
        >
          <ViewSidebarOutlinedIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={styles.panelBody}>
        {/*<Button startIcon={<AddIcon />} sx={treeStyles.addButton}>*/}
        {/*  {translate('editorMode.add', 'Add')}*/}
        {/*</Button>*/}

        {EDITOR_GROUPS.map((group) => {
          if (group.status === 'hidden') {
            return null;
          }

          const isDisabled = group.status === 'coming_soon';
          const isSpotsGroup =
            group.nodeKind === EditorNodeKind.SPOT &&
            group.id === EditorGroupId.SPOTS;
          const isEnvGroup =
            group.nodeKind === EditorNodeKind.MAP_AND_COLORS &&
            group.id === EditorGroupId.ENVIRONMENT;
          const isCollapsible = isSpotsGroup;
          const isCollapsed = isCollapsible && !!collapsedGroups[group.id];

          return (
            <Box key={group.id} sx={treeStyles.groupSection}>
              <Box
                sx={[
                  treeStyles.groupLabelRow,
                  isCollapsible && treeStyles.groupLabelRowCollapsible,
                ]}
                onClick={() => isCollapsible && toggleGroup(group.id)}
              >
                {isCollapsible && (
                  <ExpandMoreOutlinedIcon
                    sx={[
                      treeStyles.groupChevron,
                      isCollapsed && treeStyles.groupChevronCollapsed,
                    ]}
                  />
                )}
                <Typography sx={treeStyles.groupLabel}>
                  {translate(group.labelKey, group.labelFallback)}
                </Typography>
                {isSpotsGroup && spots.length > 0 && (
                  <Typography sx={treeStyles.groupCount}>
                    {spots.length}
                  </Typography>
                )}
                {isDisabled && (
                  <Typography sx={treeStyles.comingSoonBadge}>
                    {translate('editorMode.comingSoon', 'coming soon')}
                  </Typography>
                )}
              </Box>

              {isEnvGroup && (
                <Box
                  sx={[
                    treeStyles.node,
                    isMapColorsSelected && treeStyles.nodeSelected,
                  ]}
                  onClick={selectMapAndColors}
                >
                  <PaletteOutlinedIcon sx={treeStyles.nodeIcon} />
                  <Typography sx={treeStyles.nodeLabel}>
                    {translate('editorMode.mapAndColors', 'Map and colors')}
                  </Typography>
                </Box>
              )}

              {isSpotsGroup && !isCollapsed && spots.length === 0 && (
                <Typography sx={treeStyles.emptyText}>
                  {translate('editorMode.noSpots', 'No spots in this branding')}
                </Typography>
              )}

              {isSpotsGroup &&
                !isCollapsed &&
                spots.map((spot, idx) => {
                  const ueId = normalizeUeIdentifier(spot.ueIdentifier);
                  const key = ueId || `spot-${idx}`;
                  const type = getSpotType(spot);
                  const isSelected =
                    !!selectedSpotUeId &&
                    ueId === normalizeUeIdentifier(selectedSpotUeId);
                  const isHidden = spot.isActive === false;
                  const name =
                    typeof spot.name === 'string' && spot.name.length > 0
                      ? spot.name
                      : ueId || translate('adminToolbox.unnamed', 'Unnamed');

                  return (
                    <Tooltip
                      key={key}
                      title={name}
                      placement="right"
                      enterDelay={350}
                      arrow
                    >
                      <Box
                        ref={isSelected ? selectedNodeRef : undefined}
                        sx={[
                          treeStyles.node,
                          isSelected && treeStyles.nodeSelected,
                        ]}
                        onClick={() => ueId && handleSpotSelect(spot, ueId)}
                      >
                        {getSpotTypeIcon(type, treeStyles.nodeIcon)}
                        <Typography sx={treeStyles.nodeLabel}>
                          {name}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => handleToggleVisibility(e, spot)}
                          sx={[
                            treeStyles.nodeEye,
                            isHidden && treeStyles.nodeEyeHidden,
                          ]}
                          aria-label={translate(
                            'editorMode.toggleVisibility',
                            'Toggle visibility',
                          )}
                        >
                          {isHidden ? (
                            <VisibilityOffOutlinedIcon fontSize="small" />
                          ) : (
                            <VisibilityOutlinedIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Box>
                    </Tooltip>
                  );
                })}

              {isDisabled && (
                <Box sx={[treeStyles.node, treeStyles.nodeDisabled]}>
                  <Typography sx={treeStyles.nodeLabel}>—</Typography>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default SceneTree;
